// src\services\axiosInstance.ts
import axios, { AxiosError, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import type { Dispatch, UnknownAction } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';
import { API_URL } from '../Config';
import { incrementActiveRequests, decrementActiveRequests } from '../stores/loadingSlice';
import { showError } from '../utils/mySwal';

type SoftswitchRequestConfig = InternalAxiosRequestConfig & {
    suppressAuthRedirect?: boolean;
};

const axiosInstance = axios.create({
    baseURL: API_URL,
    timeout: 1000 * 60,
});

type AxiosStoreBindings = {
    dispatch: Dispatch<UnknownAction>;
    createLogoutAction: () => UnknownAction;
};

let storeBindings: AxiosStoreBindings | null = null;

export const configureAxiosStore = (
    dispatch: Dispatch<UnknownAction>,
    createLogoutAction: () => UnknownAction,
) => {
    storeBindings = { dispatch, createLogoutAction };
};

let isAlreadyLoggedOut = false; // Prevent multiple logout flows/toasts

axiosInstance.interceptors.request.use((config: SoftswitchRequestConfig) => {
    storeBindings?.dispatch(incrementActiveRequests());
    return config
});

axiosInstance.interceptors.response.use(
    (response: AxiosResponse) => {
        storeBindings?.dispatch(decrementActiveRequests());

        const cfg = (response.config || {}) as InternalAxiosRequestConfig;
        // const method = cfg.method?.toLowerCase();
        const method = (cfg?.method ?? 'get').toLowerCase();
        const shouldShowToast = !cfg.hideToast && method !== 'get';

        // By default: show success toast if server returned a message
        if (shouldShowToast && response.data?.message) {
            toast.success(response.data.message, { toastId: 'toast-top-right-success' });
        }

        return response;
    },
    (error: AxiosError) => {
        storeBindings?.dispatch(decrementActiveRequests());

        if (error.response) {
            const status = error.response.status;
            const { message } = error.response.data as any;
            const cfg = (error.config || {}) as SoftswitchRequestConfig;

            const responseData = error.response.data as any;
            const isSessionLimitReached = status === 409 && responseData?.data?.code === "SESSION_LIMIT_REACHED";

            if (status === 401) {
                if (cfg?.suppressAuthRedirect) {
                    if (message) toast.error(message, { toastId: 'toast-top-right-error' });
                } else if (!isAlreadyLoggedOut) {
                    isAlreadyLoggedOut = true;
                    if (storeBindings) {
                        storeBindings.dispatch(storeBindings.createLogoutAction());
                    }
                    // Reset flag so later 401s can trigger again after a delay
                    setTimeout(() => (isAlreadyLoggedOut = false), 3000);

                    if (message) toast.error(message, { toastId: 'toast-top-right-error' });
                    window.location.replace('/');
                }
            } else if (!isSessionLimitReached) {
                if (message) {
                    showError(message);
                }
            }
        } else if (error.request) {
            // toast.error('No response from server. Please check your network.', { toastId: 'toast-top-right-err-first' });
            showError('No response from server. Please check your network');
        } else {
            showError(error?.message);
        }

        return Promise.reject(error);
    }
);

export default axiosInstance;


/* 
// GET: no success toast (default)
axiosInstance.get('/users');

// GET but you still don't want success toast (redundant, but valid)
axiosInstance.get('/users', { hideToast: true });

// POST: will show success toast if server returns { message: '...' }
axiosInstance.post('/users', payload);

// POST but suppress success toast
axiosInstance.post('/users', payload, { hideToast: true }); 
*/
